/**
 * The estimate totals ladder — docs/adr/0011-the-totals-ladder.md.
 *
 * This is the reason the application exists. It was reverse-engineered from the
 * system the company runs on today and reproduces the reference job (Justin
 * Gamble, #4014911) to the cent; the golden tests in test/ladder.test.ts pin
 * every one of those figures.
 *
 * Rules that are easy to get wrong, all of them load-bearing:
 *
 *  - Only line items with `work_doing` count toward the contract. The rest are
 *    reported as "Work Not Doing" and excluded from the grand total entirely.
 *  - `estimate_items.customer_price` is the LINE total, not a unit price.
 *  - Sales tax is NOT on the estimate. It is a material cost, charged at the
 *    supplier's rate on the material order, and reaches these numbers only
 *    through `materialCost`. The contract does not add tax on top. Confirmed by
 *    Jonathon on 2026-08-09: "sales tax only belongs on material orders. it's
 *    not customer facing. it's strictly a material sales tax."
 *  - Cost and revenue are both estimate-level (ADR 0030). An estimate is scored
 *    against its own orders and its own expenses, so switching which one is
 *    active does not re-score one pool of costs against a different price.
 *    Confirmed by Jonathon on 2026-08-09: "I want the costs for that estimate to
 *    only be associated with that estimate. Each estimate tab does not affect
 *    the other one." The roof layout is the one exception and is still read per
 *    job — the building is measured once, however many ways it is priced.
 *  - Some expenses are computed, not typed — see autoExpenses.ts. They are
 *    percentages of revenue, so they are worked out here rather than stored.
 *  - The GC fee is both a cost and revenue. The company bills it on at cost, so
 *    it lands in `otherExpenses` and again in `grandTotal`, and cancels out of
 *    gross profit. Percentage rules are taken on the contract BEFORE the fee is
 *    added, or they would chase their own tail.
 *  - Everything is integer cents. See money.ts.
 */

import {
  computeAutoExpenses,
  gcFee,
  isExpenseRemoved,
  overriddenCents,
  totalAutoExpenses,
  type AutoExpense,
  type AutoExpenseInsuranceLine,
} from "./autoExpenses.js";
import type { InsuredCrew, TradeList } from "./insurance.js";
import { crewLabel } from "./crewName.js";
import { mountainBrookFeeApplies } from "./permit.js";
import type { SignedContract } from "./quickbooks.js";
import {
  addCents,
  type Cents,
  cents,
  multiplyCents,
  percentOf,
  ratio,
  sumBy,
  toCents,
  toPercent,
} from "./money.js";
import type {
  ChangeOrder,
  CostDocument,
  Estimate,
  EstimateItem,
  JobExpense,
} from "./types.js";

/** Every rung of the ladder, in the order the estimate screen shows them. */
export interface LadderTotals {
  /** Sum of customer_price where work_doing. */
  lineTotal: Cents;
  /** Sum of change_orders.amount. */
  changeOrders: Cents;
  /** Sum of estimate_items.discount on lines we are doing. */
  lineDiscount: Cents;
  /** Sum of customer_price on lines marked is_upgrade (reporting only). */
  upgrades: Cents;
  /** Sum of customer_price where NOT work_doing. Excluded from the total. */
  workNotDoing: Cents;
  /** estimates.office_discount. */
  officeDiscount: Cents;
  /** The contract before the GC fee is billed on. */
  contractBase: Cents;
  /** The GC fee, billed on at cost. Zero under $50,000 of revenue. */
  gcFee: Cents;
  /** The contract amount. */
  grandTotal: Cents;
  /**
   * The 3% the customer added by paying on a credit card.
   *
   * Charged on the payment, not the contract: half of a $20,000 roof on a card
   * is $300 and a balance paid by bank transfer adds nothing. So this is a sum
   * of what was actually charged, not a percentage of anything on this screen.
   *
   * Deliberately **after** `grandTotal` and not inside it. It is not work sold,
   * so it must not attract the GC fee, and it is not profit, so it must not
   * move the margin, the overhead or a rep's commission — the real processor
   * fee lands in `otherExpenses` (ADR 0095) and cancels it out to within a few
   * dollars. Every job signed before 2026-09-06 has none, which is why the
   * golden tests still reproduce #4014911 to the cent.
   */
  cardSurcharge: Cents;
  /** grandTotal + cardSurcharge: what the customer is actually billed. */
  customerTotal: Cents;
  /** Sum of the job's material order totals. */
  materialCost: Cents;
  /** Sum of the job's work order totals. */
  laborCost: Cents;
  /** Expenses a person typed in. */
  manualExpenses: Cents;
  /** Typed expenses plus the computed ones. */
  otherExpenses: Cents;
  /** Credits for returned material. Positive here; subtracted from cost. */
  returns: Cents;
  /**
   * Material + labour + expenses, less returns: what the job costs us before
   * any profit. The same lines the ladder shows, added up, so nobody has to.
   */
  totalCost: Cents;
  /** The computed lines, for the Other Expenses table. */
  autoExpenses: readonly AutoExpense[];
  grossProfit: Cents;
  /** grandTotal x job_overhead_pct. 10% by default. */
  jobOverhead: Cents;
  /** Adjusted gross profit. */
  agp: Cents;
  /** grossProfit / grandTotal, 0 when there is no contract. */
  margin: number;
  /** agp / grandTotal. */
  agpMargin: number;
}

/** Everything the ladder reads. All of it belongs to one estimate (ADR 0030). */
export interface LadderInput {
  /** Pin insurance certificate evaluation for a preview; absent uses today. */
  asOfDate?: string;
  estimate: Pick<Estimate, "office_discount" | "job_overhead_pct"> & {
    trade?: string | null;
    roof_type?: string | null;
    /** Silver Pledge, Golden Pledge or none. Defaults to Silver Pledge. */
    warranty?: string | null | undefined;
    /** Whether this estimate hires a crane. Commercial jobs only. */
    crane?: boolean | null;
    /** Days of crane, when the roof's own answer has been overridden. */
    crane_days?: unknown;
    /** When it was made: Mountain Brook's fee is charged from 2026-10-16. */
    created_at?: string | null;
  };
  /** 'residential' or 'commercial' — drives the lift and dumpster. */
  jobType?: string | null | undefined;
  /**
   * Where the job is, which decides whose permit fee it pays (ADR 0289).
   * Absent charges the usual 1%, which is what every estimate paid before.
   */
  place?:
    | {
        address?: string | null;
        city?: string | null;
        contact?: { city?: string | null } | null;
      }
    | null
    | undefined;
  /**
   * The signature this estimate was sold on, `signatureFor`; absent or null
   * while nobody has signed it. Read only by the Mountain Brook permit, which
   * is worked out on the estimate's own price until it is signed and on what
   * the customer signed from then on (ADR 0289).
   */
  signature?:
    | Pick<SignedContract, "total" | "acceptedAt" | "estimateIds">
    | null
    | undefined;
  /** The job's roof layouts, for the warranty, the lift and the dumpster. */
  layouts?: readonly {
    squares?: unknown;
    waste_pct?: unknown;
    layers?: unknown;
  }[];
  /**
   * Amounts typed over the calculated expenses, keyed by expense key — see
   * `applyOverrides`. The GC fee is read from here twice on purpose: it is a
   * cost and it is revenue.
   */
  autoExpenseOverrides?: Readonly<Record<string, unknown>> | null | undefined;
  /**
   * Expenses struck off this job, keyed by expense key — see `applyRemovals`.
   * The GC fee is read from here twice for the same reason its override is:
   * it is a cost and it is revenue.
   */
  autoExpenseRemovals?: Readonly<Record<string, unknown>> | null | undefined;
  /**
   * The crew on each work order, in the same order as `workOrders`.
   *
   * Its insurance columns as well as its name: the liability and comp charged
   * on an order depend on whether that crew's certificates are current, so a
   * name on its own is not enough any more. See `insurance.ts`.
   */
  workOrderCrews?: readonly (InsuredCrew | null)[];
  /**
   * Insurance added by hand, for a sub with no work order on this estimate.
   *
   * Rows off `estimate_insurance_lines`, crew and all. Only the labour is
   * stored — the liability and comp on it are worked out at the same rates a
   * work order's are, so an estimate priced before anybody raised an order
   * still carries its insurance. See `AutoExpenseInsuranceLine`.
   */
  insuranceLines?: readonly {
    id: string;
    /** Which crew — how a line is matched to that crew's work order. */
    crew_id?: string | null;
    /** Dollars, like every other money column on a row. */
    labor_amount: unknown;
    trade?: string | null;
    crew?: (InsuredCrew & { name?: string | null }) | null;
  }[];
  items: readonly Pick<
    EstimateItem,
    "customer_price" | "discount" | "is_upgrade" | "work_doing"
  >[];
  /**
   * `signed_at` alongside the amount: every line counts towards the ladder
   * whether it is signed or not (ADR 0168), but the money screens need to
   * know which of them the customer has actually agreed to —
   * `signedChangeOrderTotal` in changeOrder.ts, and `invoiceAmount`.
   */
  changeOrders: readonly (Pick<ChangeOrder, "amount"> &
    Partial<Pick<ChangeOrder, "signed_at">>)[];
  materialOrders: readonly CostDocument[];
  /**
   * The work orders. `trades` is read for the insurance class, so a caller
   * that leaves it off gets the roofing rate — the dearest, and the right way
   * to be wrong.
   */
  workOrders: readonly (CostDocument & {
    trades?: TradeList;
    /** Matched against a hand-added insurance line's crew. See `crewsWithBoth`. */
    crew_id?: string | null;
  })[];
  expenses: readonly Pick<JobExpense, "amount">[];
  /**
   * Card fees this customer has been charged on this estimate.
   *
   * `mercury_payment_requests.surcharge_cents`, fetched by `estimate_id` like
   * every other bucket the ladder reads (ADR 0030) — which is why that table
   * carries an estimate at all.
   */
  cardSurcharges?: readonly { amountCents: unknown }[];
  /**
   * Material sent back to the supplier. Amounts are positive — the receipt
   * says $450, not −$450 — and this subtracts them. The tax comes back with
   * the material, so it is part of the credit.
   */
  returns?: readonly {
    amount: unknown;
    tax_rate?: unknown;
    credit_amount?: unknown;
  }[];
}

/**
 * The hand-added insurance lines, in the shape `computeAutoExpenses` wants.
 *
 * One place rather than three: the ladder works them out twice — once for the
 * totals and once for every pass of `contractBaseForMargin` — and a rounding
 * or a fallback that differed between them would put a margin on screen that
 * the job could not reach.
 */
function insuranceLinesFor(
  input: LadderInput,
): readonly AutoExpenseInsuranceLine[] {
  return (input.insuranceLines ?? []).map((line) => ({
    id: line.id,
    crewId: line.crew_id ?? null,
    crewName: crewLabel(line.crew),
    crew: line.crew ?? null,
    trades: line.trade ?? null,
    paid: toCents(line.labor_amount),
  }));
}

/**
 * What a return is actually worth back.
 *
 * The supplier credits the tax along with the material, so a $447.96 return at
 * 9% is $488.28 off the job. Taxed the same way a material order is taxed, for
 * the same reason: it is the same money going the other way.
 *
 * Unless somebody typed the credit off the supplier's memo
 * (`returnCreditIsTyped`), which then stands as typed. Jonathon, 2026-09-30:
 * "we need to be able to edit their totals so it's easy to reconcile". A
 * memo's tax is rounded its own way, and no quantity, price and rate is
 * guaranteed to land on its figure to the cent. ADR 0244.
 */
export function returnCredit(row: {
  amount: unknown;
  tax_rate?: unknown;
  credit_amount?: unknown;
}): Cents {
  if (returnCreditIsTyped(row)) return toCents(row.credit_amount);
  const net = toCents(row.amount);
  return addCents(net, percentOf(net, toPercent(row.tax_rate)));
}

/** Whether a return's credit was typed rather than worked out. */
export function returnCreditIsTyped(row: { credit_amount?: unknown }): boolean {
  return (
    row.credit_amount !== null &&
    row.credit_amount !== undefined &&
    String(row.credit_amount).trim() !== ""
  );
}

/**
 * The total of a production document (material or work order):
 * `sum(qty x unit_price) + tax + reconcile_amt`, where tax is the document's
 * own rate applied to the line sum. ADR 0011.
 */
export function documentSubtotal(doc: CostDocument): Cents {
  return sumBy(doc.items ?? [], (item) =>
    multiplyCents(toCents(item.unit_price), Number(item.qty ?? 0)),
  );
}

/**
 * Tax on a production document: subtotal x rate, unless somebody typed the
 * amount off the supplier's bill (`taxIsTyped`), which then stands as typed.
 *
 * Jonathon, 2026-09-30: "we need to be able to manually edit tax on material
 * orders when needed". A bill's tax is rounded per line, or leaves out an
 * exempt part, and the rate cannot say that. ADR 0243.
 */
export function documentTax(doc: CostDocument): Cents {
  if (taxIsTyped(doc)) return toCents(doc.tax_amount);
  return percentOf(documentSubtotal(doc), toPercent(doc.tax_rate));
}

/** Whether the tax was typed in dollars rather than worked out from the rate. */
export function taxIsTyped(doc: Pick<CostDocument, "tax_amount">): boolean {
  return (
    doc.tax_amount !== null &&
    doc.tax_amount !== undefined &&
    String(doc.tax_amount).trim() !== ""
  );
}

/** subtotal + tax + reconcile_amt. */
export function documentTotal(doc: CostDocument): Cents {
  return addCents(
    documentSubtotal(doc),
    documentTax(doc),
    toCents(doc.reconcile_amt),
  );
}

/**
 * The four sums a contract is made of, and nothing else.
 *
 * Pulled out of `computeLadder` so that a screen which only wants to know what
 * a job is worth — a pipeline card, the strip above a list — can ask without
 * fetching every material order and expense behind it. `computeLadder` calls
 * this too, so there is exactly one definition of the contract amount and the
 * golden tests in test/ladder.test.ts pin both at once.
 */
export interface ContractParts {
  /** Sum of `customer_price` on the lines we are doing. */
  lineTotal: Cents;
  /** Sum of `discount` on those same lines. */
  lineDiscount: Cents;
  /** Sum of `change_orders.amount`. */
  changeOrders: Cents;
  /** `estimates.office_discount`. */
  officeDiscount: Cents;
  /** 'residential' or 'commercial' — only commercial pays a GC fee. */
  jobType?: string | null | undefined;
  /**
   * A GC fee somebody has typed over the calculated one.
   *
   * It has to be known here as well as in `computeAutoExpenses`, because the
   * fee is the one expense that is also revenue — billed on at cost, so it
   * appears on both sides and cancels out of gross profit. Overriding only the
   * cost side would leave the customer paying the calculated fee while the job
   * carries the typed one, and the difference would show up as profit the
   * company never made.
   */
  gcFeeOverride?: unknown;
  /**
   * Whether the GC fee has been struck off this job altogether.
   *
   * Separate from an override of zero, and it has to be: an override is a
   * number the company is charging, and this is the absence of the line. Both
   * come out at $0 on the cost side, but only this one takes the fee off the
   * customer's contract as a line that was never there.
   */
  gcFeeRemoved?: unknown;
}

export interface ContractAmount {
  /** The contract before the GC fee is billed on. */
  contractBase: Cents;
  gcFee: Cents;
  /** What the customer agreed to pay. */
  grandTotal: Cents;
}

export function contractAmount(parts: ContractParts): ContractAmount {
  const contractBase = cents(
    parts.lineTotal +
      parts.changeOrders -
      parts.lineDiscount -
      parts.officeDiscount,
  );
  const fee = isExpenseRemoved(parts.gcFeeRemoved)
    ? (0 as Cents)
    : overriddenCents(parts.gcFeeOverride, gcFee(contractBase, parts.jobType));
  return { contractBase, gcFee: fee, grandTotal: cents(contractBase + fee) };
}

/**
 * The price the Mountain Brook permit is worked out on (ADR 0289).
 *
 * Jonathon, 2026-10-20: "it needs to be based on the estimate when it's first
 * built and then change to the signed amount". Signed, it is what the
 * customer signed, frozen on their signature, so a change order signed since
 * does not move it, and it is the same figure the pink form's valuation box
 * is filled with (`contractAsSigned`). Unsigned, it is the price they would
 * sign: the contract with the GC fee billed on, the figure a signature
 * freezes, so the permit does not move at the moment of signing. One
 * signature that bought several options has one total for all of them, which
 * cannot be split, so there it is this option's own price, less every change
 * order not signed by the time they signed, with its GC fee.
 *
 * `contractBase` is the price before the GC fee. The fee is worked out from it
 * alone, never from a cost, so pricing to a margin cannot chase its own tail.
 */
function mountainBrookPermitRevenue(
  input: LadderInput,
  contractBase: Cents,
): Cents {
  const signature = input.signature;
  if (signature && signature.total > 0) {
    if (new Set(signature.estimateIds).size <= 1) return signature.total;
    const signedAt = Date.parse(signature.acceptedAt);
    const since = sumBy(
      input.changeOrders.filter(
        (change) =>
          !change.signed_at || !(Date.parse(change.signed_at) <= signedAt),
      ),
      (change) => toCents(change.amount),
    );
    return withGcFee(input, cents(contractBase - since));
  }
  return withGcFee(input, contractBase);
}

/** A price before the GC fee, with the fee billed on as `contractAmount` does. */
function withGcFee(input: LadderInput, base: Cents): Cents {
  return contractAmount({
    gcFeeOverride: input.autoExpenseOverrides?.gc_fee,
    gcFeeRemoved: input.autoExpenseRemovals?.gc_fee,
    lineTotal: base,
    lineDiscount: 0 as Cents,
    changeOrders: 0 as Cents,
    officeDiscount: 0 as Cents,
    jobType: input.jobType,
  }).grandTotal;
}

/** Compute every rung of the ladder. Pure: same input, same numbers, always. */
export function computeLadder(input: LadderInput): LadderTotals {
  const {
    estimate,
    items,
    changeOrders,
    materialOrders,
    workOrders,
    expenses,
  } = input;

  const doing = items.filter((i) => i.work_doing);
  const notDoing = items.filter((i) => !i.work_doing);

  const lineTotal = sumBy(doing, (i) => toCents(i.customer_price));
  const lineDiscount = sumBy(doing, (i) => toCents(i.discount));
  const upgrades = sumBy(
    doing.filter((i) => i.is_upgrade),
    (i) => toCents(i.customer_price),
  );
  const workNotDoing = sumBy(notDoing, (i) => toCents(i.customer_price));

  const changeOrderTotal = sumBy(changeOrders, (c) => toCents(c.amount));
  const officeDiscount = toCents(estimate.office_discount);

  const {
    contractBase,
    gcFee: fee,
    grandTotal,
  } = contractAmount({
    gcFeeOverride: input.autoExpenseOverrides?.gc_fee,
    gcFeeRemoved: input.autoExpenseRemovals?.gc_fee,
    lineTotal,
    lineDiscount,
    changeOrders: changeOrderTotal,
    officeDiscount,
    jobType: input.jobType,
  });

  const materialCost = sumBy(materialOrders, documentTotal);
  const laborCost = sumBy(workOrders, documentTotal);

  const autoExpenses = computeAutoExpenses({
    today: input.asOfDate,
    revenue: contractBase,
    jobType: input.jobType,
    trade: estimate.trade,
    roofType: estimate.roof_type,
    warranty: estimate.warranty,
    crane: estimate.crane,
    craneDays: estimate.crane_days,
    layouts: input.layouts ?? [],
    overrides: input.autoExpenseOverrides,
    removed: input.autoExpenseRemovals,
    mountainBrookPermit: mountainBrookFeeApplies(
      input.place,
      estimate.created_at,
    ),
    permitRevenue: mountainBrookPermitRevenue(input, contractBase),
    workOrders: workOrders.map((order, i) => ({
      crewId: order.crew_id ?? null,
      crewName: input.workOrderCrews?.[i]?.name ?? null,
      crewLabel: crewLabel(input.workOrderCrews?.[i]),
      crew: input.workOrderCrews?.[i] ?? null,
      trades: order.trades ?? null,
      total: documentTotal(order),
    })),
    insuranceLines: insuranceLinesFor(input),
  });

  const manualExpenses = sumBy(expenses, (e) => toCents(e.amount));
  const otherExpenses = addCents(
    manualExpenses,
    totalAutoExpenses(autoExpenses),
  );

  // Returned material is money we do not spend, so it comes off the cost side
  // rather than being added to revenue. Same effect on profit, but the contract
  // still says what the customer agreed to pay.
  const returns = sumBy(input.returns ?? [], returnCredit);

  const totalCost = cents(materialCost + laborCost + otherExpenses - returns);

  // `amountCents`, not `amount`: `mercury_payment_requests.surcharge_cents` is
  // already integer cents, where every other money column the ladder reads is a
  // decimal of dollars. Putting it through `toCents` would multiply a $270 fee
  // into $27,000 — the field is named for the unit precisely so that nobody
  // reaches for the wrong converter (CLAUDE.md rule 3).
  const cardSurcharge = sumBy(input.cardSurcharges ?? [], (fee) =>
    cents(Math.round(Number(fee.amountCents) || 0)),
  );

  const grossProfit = cents(grandTotal - totalCost);

  const jobOverhead = percentOf(
    grandTotal,
    toPercent(estimate.job_overhead_pct),
  );
  const agp = cents(grossProfit - jobOverhead);

  return {
    lineTotal,
    changeOrders: changeOrderTotal,
    lineDiscount,
    upgrades,
    workNotDoing,
    officeDiscount,
    contractBase,
    gcFee: fee,
    grandTotal,
    cardSurcharge,
    customerTotal: cents(grandTotal + cardSurcharge),
    materialCost,
    laborCost,
    manualExpenses,
    otherExpenses,
    returns,
    totalCost,
    autoExpenses,
    grossProfit,
    jobOverhead,
    agp,
    margin: ratio(grossProfit, grandTotal),
    agpMargin: ratio(agp, grandTotal),
  };
}

/** An empty ladder, for a job with no estimate yet. */
export function emptyLadder(): LadderTotals {
  return computeLadder({
    estimate: { office_discount: 0, job_overhead_pct: 10 },
    items: [],
    changeOrders: [],
    materialOrders: [],
    workOrders: [],
    expenses: [],
  });
}

/**
 * The contract total that would hit a target gross profit margin.
 *
 * A rep sets the margin they want and the price follows, rather than nudging
 * line items until the percentage looks right. It cannot be solved in one step:
 * general liability, the permit and the GC fee are all percentages of revenue,
 * so raising the price raises the costs too. Ten passes settle it well inside a
 * cent for any real job; the loop stops early once it stops moving.
 *
 * Every cost on the estimate is in here — material, labour, typed expenses and
 * all nine calculated ones. Jonathon, 2026-08-26: "every cost should have a
 * margin associated with it." A cost missing from this sum is a cost the
 * company gives away, and it gives it away silently, because the margin on
 * screen still reads 40%.
 *
 * Returns the target `contractBase` — what the line items and change orders
 * have to add up to, before the GC fee is billed on.
 */
export function contractBaseForMargin(
  input: LadderInput,
  targetMargin: number,
): Cents {
  if (!Number.isFinite(targetMargin) || targetMargin >= 1) return 0 as Cents;

  const materialCost = sumBy(input.materialOrders, documentTotal);
  const laborCost = sumBy(input.workOrders, documentTotal);
  const manualExpenses = sumBy(input.expenses, (e) => toCents(e.amount));
  // Credits reduce what has to be earned, exactly as they do in computeLadder.
  const returns = sumBy(input.returns ?? [], returnCredit);

  let base = cents(
    (materialCost + laborCost + manualExpenses - returns) / (1 - targetMargin),
  );

  for (let pass = 0; pass < 10; pass++) {
    const auto = computeAutoExpenses({
      today: input.asOfDate,
      revenue: base,
      jobType: input.jobType,
      trade: input.estimate.trade,
      roofType: input.estimate.roof_type,
      warranty: input.estimate.warranty,
      // The crane belongs here for the same reason every other cost does: a
      // margin is only a margin if it is taken on all of it. Left out, a
      // commercial job with a crane on it would be repriced to a number that
      // looked like 40% and earned nothing on the largest expense on the
      // estimate.
      crane: input.estimate.crane,
      craneDays: input.estimate.crane_days,
      layouts: input.layouts ?? [],
      overrides: input.autoExpenseOverrides,
      removed: input.autoExpenseRemovals,
      mountainBrookPermit: mountainBrookFeeApplies(
        input.place,
        input.estimate.created_at,
      ),
      permitRevenue: mountainBrookPermitRevenue(input, base),
      workOrders: input.workOrders.map((order, i) => ({
        crewId: order.crew_id ?? null,
        crewName: input.workOrderCrews?.[i]?.name ?? null,
        crewLabel: crewLabel(input.workOrderCrews?.[i]),
        crew: input.workOrderCrews?.[i] ?? null,
        trades: order.trades ?? null,
        total: documentTotal(order),
      })),
      insuranceLines: insuranceLinesFor(input),
    });

    // The GC fee cancels out of gross profit but still swells the contract it
    // is measured against, so it dilutes the margin. With
    //   margin = (base - costs) / (base + fee)
    // solving for base gives (costs + margin x fee) / (1 - margin).
    const fee = isExpenseRemoved(input.autoExpenseRemovals?.gc_fee)
      ? (0 as Cents)
      : overriddenCents(
          input.autoExpenseOverrides?.gc_fee,
          gcFee(base, input.jobType),
        );
    const costs =
      materialCost +
      laborCost +
      manualExpenses +
      totalAutoExpenses(auto) -
      returns -
      fee;

    const next = cents((costs + targetMargin * fee) / (1 - targetMargin));
    if (Math.abs(next - base) <= 1) return next;
    base = next;
  }

  return base;
}
